/**
 * Task Executions Page
 *
 * Enhanced execution history view with:
 * - Date range selection (Last 7 days, Last 30 days, Today, Custom)
 * - Task filter
 * - Status filter
 * - Expandable rows for details
 * - Summary statistics
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getExecutionsByDate,
  getExecutionsByDateRange,
  listScheduledTasks,
  type TaskExecution
} from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ExecutionDetailsRow } from '@/components/scheduler/ExecutionDetailsRow';
import {
  CalendarIcon,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  Search,
  Loader2
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { SubNav, type SubNavTab } from '@/components/layout/SubNav';
import { ViewGuard } from '@/components/auth/PermissionGuards';
import { PageWrapper } from '@/components/layout/PageWrapper';

const schedulerTabs: SubNavTab[] = [
  { label: "Scheduled Tasks", path: "/scheduled-tasks" },
  { label: "Execution History", path: "/task-executions" },
];

type DateRangePreset = 'today' | '7days' | '30days' | 'custom';
type StatusFilter = 'all' | 'succeeded' | 'failed' | 'running';

interface TaskExecutionsPageProps {
  onRefreshReady?: (refreshFn: () => void, isRefreshing: boolean) => void;
}

export default function TaskExecutionsPage({ onRefreshReady }: TaskExecutionsPageProps = {}) {
  // Date range state
  const [datePreset, setDatePreset] = useState<DateRangePreset>('7days');
  const [customStartDate, setCustomStartDate] = useState<Date>(subDays(new Date(), 7));
  const [customEndDate, setCustomEndDate] = useState<Date>(new Date());

  // Filters state
  const [taskFilter, setTaskFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // State for expanded parent executions (hierarchy view)
  const [expandedParentExecutions, setExpandedParentExecutions] = useState<Set<string>>(new Set());

  // Calculate actual date range based on preset
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case 'today':
        return { start: startOfDay(now), end: endOfDay(now) };
      case '7days':
        return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
      case '30days':
        return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
      case 'custom':
        return { start: startOfDay(customStartDate), end: endOfDay(customEndDate) };
    }
  }, [datePreset, customStartDate, customEndDate]);

  // Query executions based on date range
  const {
    data: executionsData,
    isLoading: executionsLoading,
    error: executionsError,
    refetch: refetchExecutions
  } = useQuery({
    queryKey: ['task-executions-range', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: async () => {
      if (datePreset === 'today') {
        // Single day query
        return getExecutionsByDate(format(dateRange.start, 'yyyy-MM-dd'));
      }
      // Multi-day query
      return getExecutionsByDateRange(dateRange.start, dateRange.end);
    },
    refetchInterval: 30000
  });

  // Query scheduled tasks for task filter dropdown
  const { data: tasksData } = useQuery({
    queryKey: ['scheduledTasks'],
    queryFn: listScheduledTasks
  });

  const handleRefresh = () => {
    refetchExecutions();
  };

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleRefresh, executionsLoading);
    }
  }, [onRefreshReady, executionsLoading]);

  // Apply client-side filters
  const filteredExecutions = useMemo(() => {
    let executions = executionsData?.executions || [];

    // Apply task filter
    if (taskFilter !== 'all') {
      executions = executions.filter(e => e.taskId === taskFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      executions = executions.filter(e => e.status === statusFilter);
    }

    // Apply search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      executions = executions.filter(e =>
        e.taskName?.toLowerCase().includes(term) ||
        e.entityMappingName?.toLowerCase().includes(term) ||
        e.batchId?.toLowerCase().includes(term) ||
        e.error?.toLowerCase().includes(term)
      );
    }

    return executions;
  }, [executionsData?.executions, taskFilter, statusFilter, searchTerm]);

  // Calculate summary statistics for filtered view
  const summaryStats = useMemo(() => {
    const total = filteredExecutions.length;
    const succeeded = filteredExecutions.filter(e => e.status === 'succeeded').length;
    const failed = filteredExecutions.filter(e => e.status === 'failed').length;
    const running = filteredExecutions.filter(e => e.status === 'running').length;

    const totalDuration = filteredExecutions.reduce((sum, e) => sum + (e.durationMs || 0), 0);
    const avgDuration = total > 0 ? Math.round(totalDuration / total) : 0;
    const successRate = total > 0 ? Math.round((succeeded / total) * 100) : 0;

    const totalRecords = filteredExecutions.reduce((sum, e) => sum + (e.recordsProcessed || 0), 0);

    return { total, succeeded, failed, running, avgDuration, successRate, totalRecords };
  }, [filteredExecutions]);

  const formatDuration = (ms?: number): string => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Toggle expand/collapse for parent executions
  const toggleParentExecution = useCallback((executionId: string) => {
    setExpandedParentExecutions(prev => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  }, []);

  // Group executions by parent/child relationship
  // Handles orphaned children (when parent is filtered out) by showing them as standalone
  const { parentExecutions, childExecutionsMap, orphanedChildren } = useMemo(() => {
    const parents: TaskExecution[] = [];
    const childMap = new Map<string, TaskExecution[]>();
    const parentIds = new Set<string>();

    // First pass: identify all parent/standalone executions
    for (const exec of filteredExecutions) {
      if (!exec.parentExecutionId) {
        parents.push(exec);
        parentIds.add(exec.executionId);
      }
    }

    // Second pass: group children and identify orphans
    const orphans: TaskExecution[] = [];
    for (const exec of filteredExecutions) {
      if (exec.parentExecutionId) {
        if (parentIds.has(exec.parentExecutionId)) {
          // Parent is in filtered set - group normally
          const existing = childMap.get(exec.parentExecutionId) || [];
          existing.push(exec);
          childMap.set(exec.parentExecutionId, existing);
        } else {
          // Parent is filtered out - treat as orphaned (show standalone)
          orphans.push(exec);
        }
      }
    }

    // Sort children by start time
    childMap.forEach((children) => {
      children.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    });

    // Sort orphans by start time
    orphans.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return { parentExecutions: parents, childExecutionsMap: childMap, orphanedChildren: orphans };
  }, [filteredExecutions]);

  const getDateRangeLabel = (): string => {
    switch (datePreset) {
      case 'today':
        return format(dateRange.start, 'MMMM d, yyyy');
      case '7days':
        return `Last 7 days`;
      case '30days':
        return `Last 30 days`;
      case 'custom':
        return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`;
    }
  };

  // Get unique tasks for filter dropdown
  const taskOptions = useMemo(() => {
    const tasks = tasksData?.tasks || [];
    return tasks.map(t => ({ id: t.id, name: t.name || 'Unknown Task' }));
  }, [tasksData?.tasks]);

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Task Executions</p>
        </div>
      </div>
    }>
    <>
      <SubNav
        tabs={schedulerTabs}
        onRefresh={handleRefresh}
        isRefreshing={executionsLoading}
      />
      <PageWrapper padding="default">
        <div className="space-y-6">
          {/* Page Header */}
          <div>
            <h1 className="text-3xl font-bold">Execution History</h1>
            <p className="text-muted-foreground mt-1">
              View scheduled task execution history and statistics
            </p>
          </div>

          {/* Filters Card */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Date Range Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Date Range:</span>
                  <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DateRangePreset)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="7days">Last 7 days</SelectItem>
                      <SelectItem value="30days">Last 30 days</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>

                  {datePreset === 'custom' && (
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-[130px] justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(customStartDate, 'MMM d')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customStartDate}
                            onSelect={(date) => date && setCustomStartDate(date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <span className="text-muted-foreground">to</span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-[130px] justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(customEndDate, 'MMM d')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customEndDate}
                            onSelect={(date) => date && setCustomEndDate(date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {/* Task Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Task:</span>
                  <Select value={taskFilter} onValueChange={setTaskFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Tasks" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tasks</SelectItem>
                      {taskOptions.map(task => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Status:</span>
                  <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="succeeded">Succeeded</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="running">Running</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search executions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Statistics */}
          {!executionsLoading && executionsData?.success && (
            <div className="grid gap-4 md:grid-cols-5">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Activity className="h-3 w-3" />
                    Total Executions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summaryStats.total}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Success Rate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className={cn(
                    "text-2xl font-bold",
                    summaryStats.successRate >= 90 ? "text-green-600" :
                    summaryStats.successRate >= 70 ? "text-yellow-600" : "text-red-600"
                  )}>
                    {summaryStats.successRate}%
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <XCircle className="h-3 w-3 text-red-600" />
                    Failed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {summaryStats.failed}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Avg Duration
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatDuration(summaryStats.avgDuration)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Records</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {summaryStats.totalRecords.toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Executions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Executions - {getDateRangeLabel()}</CardTitle>
              <CardDescription>
                {filteredExecutions.length !== executionsData?.executions?.length
                  ? `Showing ${filteredExecutions.length} of ${executionsData?.executions?.length || 0} executions`
                  : `${parentExecutions.length + orphanedChildren.length} execution${(parentExecutions.length + orphanedChildren.length) !== 1 ? 's' : ''}`
                }
                {childExecutionsMap.size > 0 && (
                  <span className="ml-1">
                    ({Array.from(childExecutionsMap.values()).reduce((sum: number, arr: TaskExecution[]) => sum + arr.length, 0)} child steps)
                  </span>
                )}
                {orphanedChildren.length > 0 && (
                  <span className="ml-1 text-muted-foreground">
                    (includes {orphanedChildren.length} filtered child task{orphanedChildren.length !== 1 ? 's' : ''})
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {executionsLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : executionsError ? (
                <div className="text-center py-8 text-red-500">
                  Error loading executions: {(executionsError as Error).message}
                </div>
              ) : filteredExecutions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No executions found matching your filters</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Task Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Records</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parentExecutions.map((execution) => {
                        const childExecutions = childExecutionsMap.get(execution.executionId) || [];
                        const hasChildren = childExecutions.length > 0;
                        const isHierarchyExpanded = expandedParentExecutions.has(execution.executionId);

                        return (
                          <React.Fragment key={execution.executionId}>
                            {/* Parent/Standalone Execution Row */}
                            <ExecutionDetailsRow
                              execution={execution}
                              isParent={hasChildren}
                              childCount={childExecutions.length}
                              isHierarchyExpanded={isHierarchyExpanded}
                              onToggleHierarchy={() => toggleParentExecution(execution.executionId)}
                            />

                            {/* Child Execution Rows (when expanded) */}
                            {isHierarchyExpanded && childExecutions.map((child, index) => (
                              <ExecutionDetailsRow
                                key={child.executionId}
                                execution={child}
                                isChild={true}
                                isLastChild={index === childExecutions.length - 1}
                              />
                            ))}
                          </React.Fragment>
                        );
                      })}

                      {/* Orphaned Child Executions (parent filtered out) */}
                      {orphanedChildren.length > 0 && (
                        <>
                          {orphanedChildren.map((execution) => (
                            <ExecutionDetailsRow
                              key={execution.executionId}
                              execution={execution}
                              isChild={true}
                              isLastChild={true}
                            />
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    </>
    </ViewGuard>
  );
}
